import React, { useEffect, useState } from "react";
import { ReactComponent as ArrowBackIcon } from "assets/images/common/svgs/arrow_back.svg";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import useUtils from "hooks/useUtils";
import { useSelector } from "react-redux";
import useResourceLibrary from "../useHooks";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import { warningToaster } from "utils/helpers/common/alert-service";
import { CreateResourceLibraryDTO } from "utils/helpers/models/resource-library/resource-library.dto";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

type FormData = {
  title: string;
  tags: string[];
  userType: string[];
};

const EditResourceLibrary = () => {
  const { register, handleSubmit, setValue, formState: { errors } } = useForm<FormData>();
  const { getQueryParams } = useUtils();
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const params = getQueryParams();
  const navigate = useNavigate();

  const [data, setData] = useState<any>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [documentName, setDocumentName] = useState<string | null>(null);

  const { uploadFile, getResourceLibraryById, editResourceLibrary } = useResourceLibrary();

  useEffect(() => {
    // Fetch resource data based on the provided ID
    getResourceLibraryById(params?.id, (fetchedData: any) => {
      if (fetchedData) {
        setData(fetchedData);
        setValue("title", fetchedData.title);
        setValue("tags", fetchedData.tags);
        setValue("userType", [
          fetchedData.isForAttendee && "Attendee",
          fetchedData.isForHost && "Host",
          fetchedData.isForAmbassador && "Ambassador",
        ].filter(Boolean));

        if (fetchedData.thumbnail) {
          setImagePreview(concatPathWithBackendUrl(fetchedData.thumbnail.fileDownloadUri));
        }

        if (fetchedData.file) {
          setDocumentName(concatPathWithBackendUrl(fetchedData.file.fileDownloadUri));
        }
      }
    });
  }, [params?.id, setValue]);

  const onSubmit = async (formData: FormData) => {
    if (!imagePreview && !data?.thumbnail) {
      warningToaster("Image is required");
      return;
    }

    // Prepare the updated data
    const updatedData: CreateResourceLibraryDTO = {
      title: formData.title,
      tags: formData.tags,
      isForHost: formData.userType.includes("Host"),
      isForAttendee: formData.userType.includes("Attendee"),
      isForAmbassador: formData.userType.includes("Ambassador"),
      thumbnailId: data?.thumbnail?.id,
      fileId: data?.file?.id,
    };

    // Handle image upload if there's a new image
    if (imagePreview) {
      const formDataToUpload = new FormData();
      const imageBlob = await fetch(imagePreview).then((res) => res.blob());
      formDataToUpload.append("file", imageBlob, "thumbnail.jpg");
      const uploadedImage: any = await uploadFile(formDataToUpload);
      updatedData.thumbnailId = uploadedImage.id;
    }

    // Handle document upload if there's a new document
    if (documentName) {
      const formDataDoc = new FormData();
      const fileBlob = await fetch(documentName).then((res) => res.blob());
      formDataDoc.append("file", fileBlob, "document.pdf");
      const uploadedDocument: any = await uploadFile(formDataDoc);
      updatedData.fileId = uploadedDocument.id;
    }

    // Send the updated data to the backend
    await editResourceLibrary(params?.id, updatedData);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'document') => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      warningToaster(`${type === 'image' ? 'Image' : 'Document'} cannot exceed 2 MB`);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === 'image') {
        setImagePreview(reader.result as string);
      } else {
        setDocumentName(file.name);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <h1 className="text-base text-primaryText font-bold flex items-center gap-3">
        <span
          onClick={() => navigate(-1)}
          className="bg-transparentBlueBg w-[44px] h-[44px] rounded-full flex items-center justify-center cursor-pointer"
        >
          <ArrowBackIcon />
        </span>
        Edit Resource
      </h1>
      <div className="mt-4">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="bg-white p-6 rounded-lg space-y-6">
            <div className="flex flex-col gap-1">
              <label className="text-labelText font-semibold">Resource name</label>
              <input
                type="text"
                placeholder="How to Improve Your Communication and So..."
                {...register("title", { required: "Resource name is required" })}
                className="w-full h-[51px] px-4 border border-lightGrayBorder rounded-lg outline-none"
              />
              {errors.title && <span className="text-red-500 text-sm">{errors.title.message}</span>}
            </div>

            <div className="flex items-center gap-8 mt-4">
              {/* Image Upload */}
              <div className="flex items-center gap-4">
                <label className="block text-labelText font-semibold mb-1">Upload Image</label>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  id="imageUpload"
                  onChange={(e) => handleFileUpload(e, 'image')}
                />
                <label
                  htmlFor="imageUpload"
                  className="cursor-pointer text-forestText w-[145px] h-[50px] flex justify-center items-center bg-paperWhiteBg rounded-full"
                >
                  + Upload
                </label>
                {imagePreview && (
                  <div className="relative w-12 h-12 mt-7">
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded" />
                    <button
                      type="button"
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                      onClick={() => setImagePreview(null)}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Document Upload */}
            <div className="flex items-center gap-8 mt-4">
              <div className="flex items-center gap-4">
                <label className="block text-labelText font-semibold mb-1">Upload Document</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  id="docUpload"
                  onChange={(e) => handleFileUpload(e, 'document')}
                />
                <label
                  htmlFor="docUpload"
                  className="cursor-pointer text-forestText w-[145px] h-[50px] flex justify-center items-center bg-paperWhiteBg rounded-full"
                >
                  + Upload
                </label>
                {documentName && (
                  <div className="flex items-center space-x-2 mt-8">
                    <div className="text-sm">{documentName}</div>
                    <button
                      type="button"
                      className="bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                      onClick={() => setDocumentName(null)}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Tags */}
            <div>
              <p className="text-labelText font-semibold mb-2">Select Resource Tags</p>
              <div className="flex flex-wrap gap-4">
                {['Instructions', 'Tips', 'Guides', 'More Instructions'].map(tag => (
                  <label key={tag} className="flex items-center gap-2 text-forestText text-lg">
                    <input
                      type="checkbox"
                      value={tag}
                      {...register("tags", { required: "At least one tag is required" })}
                      className="custom-checkbox"
                    />
                    {tag}
                  </label>
                ))}
              </div>
            </div>

            {/* User Type */}
            <div>
              <p className="text-labelText font-semibold mb-2">Select User Type</p>
              <div className="flex flex-wrap gap-4">
                {['Attendee', 'Host', 'Ambassador'].map(type => (
                  <label key={type} className="flex items-center gap-2 text-forestText text-lg">
                    <input
                      type="checkbox"
                      value={type}
                      {...register("userType", { required: "At least one user type is required" })}
                      className="custom-checkbox"
                    />
                    {type}
                  </label>
                ))}
              </div>
              {errors.userType && <p className="text-red-500 text-sm mt-1">{errors.userType.message}</p>}
            </div>
          </div>

          <button type="submit" className="mt-4 bg-darkSlateBlueBg text-white px-6 py-2 rounded">
            Save
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditResourceLibrary;
